import { useEffect, useState } from 'react'

import {
  useGetBrandNamesFromIdsQuery,
  useLazyBrandsQuery,
} from '../../../../store/api'
import type {
  MetaResponse,
  PaginatedQueryParams,
} from '../../../../store/types'
import type { Brand } from '../../../../store/types/brand'
import type { DropdownOption } from '../../types'
import AsyncPaginatedDropdown from '../async-paginate'
import CreatebleAsyncPaginatedDropdown from '../createable-async-paginate'

type BrandsDropdownProps<B extends boolean> = {
  name?: string
  onChange?: B extends true
    ? (item: DropdownOption[]) => void
    : (item: DropdownOption) => void
  isMulti?: boolean
  isClearable?: boolean
  isCreatable?: boolean
  exclude?: number | number[]
  value?: DropdownOption
  styles?: any
  components?: any
  filterValues?: any
}

const brandsToDropdownOptions = (brand: Brand): DropdownOption => ({
  label: brand.name,
  value: brand.id.toString(),
})

export default function BrandsDropdown({
  name,
  onChange,
  isMulti = false,
  isClearable = false,
  isCreatable = false,
  exclude = undefined,
  value,
  styles,
  components,
  filterValues,
}: BrandsDropdownProps<boolean>): JSX.Element {
  const [fetcher] = useLazyBrandsQuery()
  const brandsFetcher = async (params: PaginatedQueryParams) => {
    const res = await fetcher({
      ...params,
      orderBy: 'name',
      orderDirection: 'asc',
    })
    const response: { data?: DropdownOption[]; meta?: MetaResponse<Brand> } = {}
    if (exclude !== undefined) {
      if (Array.isArray(exclude)) {
        const filteredResponse: any = res.data?.data
          .filter((brand: any) => !exclude.includes(brand?.id))
          .map(brandsToDropdownOptions)
        response.data = filteredResponse
      } else {
        const filteredResponse: any = res.data?.data
          .filter((brand: any) => brand?.id !== exclude)
          .map(brandsToDropdownOptions)
        response.data = filteredResponse
      }
    } else {
      response.data = res.data?.data.map(brandsToDropdownOptions)
    }
    response.meta = res.data?.meta

    return response
  }
  const [defaultBrandValues, setDefaultBrandValues] = useState<
    Array<{ label: string; value: string }>
  >([])

  const { data: brandNames } = useGetBrandNamesFromIdsQuery(
    { ids: filterValues },
    { skip: !filterValues },
  )

  useEffect(() => {
    setDefaultBrandValues(
      brandNames?.map((item: any) => ({
        label: item?.name,
        value: item?.id.toString(),
      })) || [],
    )
  }, [brandNames])

  let Element = AsyncPaginatedDropdown
  if (isCreatable) {
    Element = CreatebleAsyncPaginatedDropdown
  }

  return (
    <Element
      placeholder={
        isCreatable
          ? 'Type to create or select a brand…'
          : 'Type to select a brand…'
      }
      key={defaultBrandValues[0]?.value}
      fetcher={brandsFetcher}
      name={name}
      defaultValue={defaultBrandValues}
      onChange={onChange}
      isClearable={isClearable}
      isCreatable={isCreatable}
      isMulti={isMulti}
      value={value}
      styles={styles}
      components={components}
    />
  )
}
